import React, { useMemo, useState, useContext } from "react";
import { FieldConfigurationContext } from "../../../../../lib/context/FieldConfigurationContext";
import TextInput from "../../../../common/TextInput";
import SelectInput from "../../../../common/SelectInput";
import Validation from "../../../../common/Validation";
import DetailHeader from "../../components/DetailHeader";
import FormFooter from "../../components/FormFooter";
import ApprovalTeamMembersEditor from "../ApprovalTeamMembersEditor";
import { getActiveCompanyOptions } from "../AccessGroupsList";
import { SELECTED_COMPANY_STORAGE_KEY } from "../ApprovalTeamsList";
import "../RoleDetail.css";

export default function ApprovalTeamForm({ onSave, onCancel }) {
  const { createApprovalTeam, companies } = useContext(
    FieldConfigurationContext
  );

  const [formData, setFormData] = useState({
    name: "",
    company_users: [],
  });
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const companyOptions = useMemo(
    () =>
      getActiveCompanyOptions(companies, companies.configureApprovalGroupsData),
    [
      companies.data,
      companies.codesMap,
      companies.enabledData,
      companies.configureApprovalGroupsData,
    ]
  );
  const isMultiCompany = companyOptions.length > 1;

  const [selectedCompanyUuid, setSelectedCompanyUuid] = useState(() => {
    const stored = localStorage.getItem(SELECTED_COMPANY_STORAGE_KEY);
    if (stored && companyOptions.some((opt) => opt.value === stored)) {
      return stored;
    }
    return companyOptions[0]?.value || "";
  });

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name?.trim()) {
      newErrors.name = "Name is required.";
    } else if (formData.name.trim().length > 100) {
      newErrors.name = "Name must be 100 characters or fewer.";
    }
    if (isMultiCompany && !selectedCompanyUuid) {
      newErrors.company = "Company is required.";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCancel = () => {
    if (window.confirm("Are you sure you want to cancel?")) {
      onCancel();
    }
  };

  const handleSave = async () => {
    if (!validate()) return;
    setIsSaving(true);
    setSaveError(null);

    const payload = {
      name: formData.name.trim(),
      company_users: formData.company_users,
    };

    const result = await createApprovalTeam(payload, selectedCompanyUuid);

    setIsSaving(false);

    if (result?.success) {
      if (selectedCompanyUuid) {
        localStorage.setItem(SELECTED_COMPANY_STORAGE_KEY, selectedCompanyUuid);
      }
      onSave && onSave(result.data);
    } else {
      setSaveError(result?.error || "Failed to save approval team.");
    }
  };

  return (
    <div className="role-detail">
      <DetailHeader
        title="Add Approval Team"
        onBack={handleCancel}
        isEditing={false}
      />

      <div className="role-detail__content">
        <div className="role-detail__section role-detail__section--info">
          <h3 className="role-detail__section-title">Team Information</h3>
          {saveError && <Validation>{saveError}</Validation>}
          <div className="role-detail__fields">
            {isMultiCompany && (
              <div className="role-detail__field">
                <label className="role-detail__label">
                  Company <span style={{ color: "#e53e3e" }}>*</span>
                </label>
                <SelectInput
                  value={selectedCompanyUuid}
                  onChange={(e) => setSelectedCompanyUuid(e.target.value)}
                  options={companyOptions}
                  placeholder="Select a company"
                />
                {errors.company && <Validation>{errors.company}</Validation>}
              </div>
            )}
            <div className="role-detail__field">
              <label className="role-detail__label">
                Team Name <span style={{ color: "#e53e3e" }}>*</span>
              </label>
              <TextInput
                value={formData.name}
                onChange={(e) => handleChange("name", e.target.value)}
                placeholder="Enter team name"
                maxLength={100}
              />
              {errors.name && <Validation>{errors.name}</Validation>}
            </div>
          </div>
        </div>

        <ApprovalTeamMembersEditor
          companyUuid={selectedCompanyUuid}
          memberUuids={formData.company_users}
          onChangeMemberUuids={(uuids) => handleChange("company_users", uuids)}
          readOnly={false}
        />
      </div>

      <FormFooter
        onCancel={handleCancel}
        onSave={handleSave}
        saveDisabled={isSaving}
        saveLabel={isSaving ? "Saving..." : "Save"}
      />
    </div>
  );
}
